import {
  RailsItem,
  RailsThumbnailItem,
} from '@sls-react/typings/services/rails/item';
import React, { useEffect, useState } from 'react';
import { favoriteItem, unfavoriteItem } from '@sls-react/api/item';
import { useSession } from '@sls-react/contexts/SessionContext';
import { useModals } from '@sls-react/contexts/ModalsContext';
import useV1Session from '@sls-react/hooks/useV1Session';
import {
  favoritedItem,
  unfavoritedItem,
} from '@sls-react/services/analytics/events/item';
import { RailsFacetItemMeta } from '@sls-react/typings/services/rails/facets';
import useKnownCookie from '@sls-react/hooks/useKnownCookie';

interface UseItemFavoriteOptions {
  meta?: RailsFacetItemMeta;
  onFavorite?: () => void;
}

export default function useItemFavorite(
  item: RailsItem | RailsThumbnailItem,
  { meta, onFavorite }: UseItemFavoriteOptions = {},
) {
  const { loggedIn } = useSession();
  const modals = useModals();
  const session = useV1Session();
  const [dontShowCookie, setDontShowCookie] = useKnownCookie(
    'seen_favorites_guidance',
  );

  const [favorited, setFavorited] = useState(!!item.favorite);
  const [favoritersCount, setFavoritersCount] = useState(item.favoriters_count);

  useEffect(() => {
    setFavorited(meta?.favorite || !!item.favorite);
    setFavoritersCount(
      // This covers a rare case where facet_items is cached and reports an outdated
      // favoriters_count. If the favoriters count is 0, but you have it favorited, it
      // shows up weird. So just hardcode it as 1
      (meta?.favorite || item.favorite) && item.favoriters_count == 0
        ? 1
        : item.favoriters_count,
    );
  }, [meta?.favorite, item.favorite, item.favoriters_count]);

  const actuallyFavorite = async () => {
    if (favorited) {
      setFavorited(false);
      setFavoritersCount(favoritersCount - 1);
      await unfavoriteItem(item.id);
      unfavoritedItem(item);
    } else {
      if (!dontShowCookie && session.data?.user.favorites_count === 0) {
        modals.openModal('savedToFavoritesGuidance', {
          itemId: item.id,
        });
        session.refetch();
        setDontShowCookie('1', {
          expires: 3000,
        });
      }

      setFavorited(true);
      setFavoritersCount(favoritersCount + 1);
      await favoriteItem(item.id);
      favoritedItem(item);
    }

    onFavorite?.();
  };

  const favorite: React.MouseEventHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    if (loggedIn) {
      actuallyFavorite();
    } else {
      modals.openModal('loginRegister', {
        contextMessage: 'Log in to add this item to My Favorites',
        defaultView: 'login',
        onSuccessfulLogin: actuallyFavorite,
        src: 'item',
      });
    }
  };

  return {
    favorite,
    favorited,
    favoritersCount,
  };
}
